import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Typography,
  Box,
  Alert,
  MenuItem,
} from '@mui/material';
import { Report as ReportIcon } from '@mui/icons-material';

// Import your existing styled components
import {
  ActionButton,
  StyledFormControl,
  StyledSelect,
  honeyColors,
} from './styled';

const CommunityReportDialog = ({ 
  open, 
  onClose, 
  onSubmit, 
  communityName,
  loading = false 
}) => {
  const [formData, setFormData] = useState({
    reason: '',
    description: '',
    report_type: 'other'
  });
  
  const [errors, setErrors] = useState({});

  const reportTypes = [
    { value: 'spam', label: 'Spam or Promotional Content' },
    { value: 'harassment', label: 'Harassment or Bullying' },
    { value: 'inappropriate_content', label: 'Inappropriate Content' },
    { value: 'fake_information', label: 'Misinformation or Fake News' },
    { value: 'hate_speech', label: 'Hate Speech or Discrimination' },
    { value: 'violence', label: 'Violence or Threats' },
    { value: 'privacy_violation', label: 'Privacy Violation' },
    { value: 'other', label: 'Other' }
  ];

  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
    
    if (errors[field]) {
      setErrors(prev => ({
        ...prev,
        [field]: ''
      }));
    }
  };

  const validateForm = () => {
    const newErrors = {};
    
    if (!formData.reason.trim()) {
      newErrors.reason = 'Reason is required';
    }
    
    if (formData.reason.trim().length < 10) {
      newErrors.reason = 'Please provide a more detailed reason (at least 10 characters)';
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = () => {
    if (validateForm()) {
      onSubmit({
        reason: formData.reason.trim(),
        description: formData.description.trim(),
        report_type: formData.report_type
      });
    }
  };

  const handleClose = () => {
    setFormData({
      reason: '',
      description: '',
      report_type: 'other'
    });
    setErrors({});
    onClose();
  };

  return (
    <Dialog 
      open={open} 
      onClose={handleClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: '20px',
          border: `2px solid ${honeyColors.border}`,
          boxShadow: honeyColors.shadowMedium,
        }
      }}
    >
      <DialogTitle sx={{
        backgroundColor: honeyColors.surface,
        borderBottom: `2px solid ${honeyColors.border}`,
        position: 'relative',
        '&::before': {
          content: '""',
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          height: '4px',
          background: honeyColors.gradientPrimary,
        }
      }}>
        <Box display="flex" alignItems="center" gap={2}>
          <ReportIcon sx={{ color: honeyColors.warning }} />
          <Typography variant="h6" sx={{ color: honeyColors.textPrimary, fontWeight: 700 }}>
            Report Community
          </Typography>
        </Box>
        {communityName && (
          <Typography variant="body2" sx={{ color: honeyColors.textSecondary, mt: 1 }}>
            Reporting: {communityName}
          </Typography>
        )}
      </DialogTitle>
      
      <DialogContent sx={{ backgroundColor: honeyColors.surface, p: 4 }}>
        <Alert 
          severity="info" 
          sx={{ 
            mb: 3,
            borderRadius: '12px',
            backgroundColor: honeyColors.surfaceHoney,
            border: `2px solid ${honeyColors.info}`,
            color: honeyColors.textPrimary
          }}
        >
          We take community safety seriously. Please provide accurate information 
          about why you're reporting this community.
        </Alert>

        <Box display="flex" flexDirection="column" gap={3}>
          <StyledFormControl fullWidth>
            <Typography variant="body2" sx={{ 
              color: honeyColors.textSecondary, 
              fontWeight: 600, 
              mb: 1 
            }}>
              Report Type
            </Typography>
            <StyledSelect
              value={formData.report_type}
              onChange={(e) => handleInputChange('report_type', e.target.value)}
            >
              {reportTypes.map((type) => (
                <MenuItem key={type.value} value={type.value}>
                  {type.label}
                </MenuItem>
              ))}
            </StyledSelect>
          </StyledFormControl>

          <Box>
            <Typography variant="body2" sx={{ 
              color: honeyColors.textSecondary, 
              fontWeight: 600, 
              mb: 1 
            }}>
              Reason for Report *
            </Typography>
            <textarea
              rows={3}
              value={formData.reason}
              onChange={(e) => handleInputChange('reason', e.target.value)}
              placeholder="Describe the issue you've encountered..."
              style={{
                width: '100%',
                padding: '12px',
                borderRadius: '12px',
                backgroundColor: honeyColors.surfaceHoney,
                border: `2px solid ${errors.reason ? honeyColors.error : honeyColors.border}`,
                color: honeyColors.textPrimary,
                fontFamily: 'inherit',
                resize: 'none',
                outline: 'none',
                transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
              }}
              onFocus={(e) => {
                if (!errors.reason) {
                  e.target.style.borderColor = honeyColors.primary;
                  e.target.style.boxShadow = honeyColors.shadowGlow;
                }
              }}
              onBlur={(e) => {
                if (!errors.reason) {
                  e.target.style.borderColor = honeyColors.border;
                  e.target.style.boxShadow = 'none';
                }
              }}
            />
            {errors.reason && (
              <Typography variant="caption" sx={{ color: honeyColors.error, mt: 1 }}>
                {errors.reason}
              </Typography>
            )}
          </Box>

          <Box>
            <Typography variant="body2" sx={{ 
              color: honeyColors.textSecondary, 
              fontWeight: 600, 
              mb: 1 
            }}>
              Additional Details (Optional)
            </Typography>
            <textarea
              rows={4}
              value={formData.description}
              onChange={(e) => handleInputChange('description', e.target.value)}
              placeholder="Any additional information that might help us understand the issue..."
              style={{
                width: '100%',
                padding: '12px',
                borderRadius: '12px',
                backgroundColor: honeyColors.surfaceHoney,
                border: `2px solid ${honeyColors.border}`,
                color: honeyColors.textPrimary,
                fontFamily: 'inherit',
                resize: 'none',
                outline: 'none',
                transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
              }}
              onFocus={(e) => {
                e.target.style.borderColor = honeyColors.primary;
                e.target.style.boxShadow = honeyColors.shadowGlow;
              }}
              onBlur={(e) => {
                e.target.style.borderColor = honeyColors.border;
                e.target.style.boxShadow = 'none';
              }}
            />
          </Box>
        </Box>
      </DialogContent>

      <DialogActions sx={{ 
        backgroundColor: honeyColors.surface,
        borderTop: `2px solid ${honeyColors.border}`,
        p: 3,
        gap: 2
      }}>
        <ActionButton 
          variant="secondary"
          onClick={handleClose}
          disabled={loading}
        >
          Cancel
        </ActionButton>
        <ActionButton 
          variant="report"
          onClick={handleSubmit}
          disabled={loading || !formData.reason.trim()}
        >
          <ReportIcon sx={{ mr: 1 }} />
          {loading ? 'Submitting...' : 'Submit Report'}
        </ActionButton>
      </DialogActions>
    </Dialog>
  );
};

export default CommunityReportDialog;